import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function Upgrade() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  // Check if user is authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/profile/`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
          }
        } catch (err) {
          console.error('Auth check failed:', err);
        }
      }
    };
    checkAuth();
  }, []);

  const handleUpgrade = async () => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem('accessToken');
    if (!token) {
      // Not logged in - redirect to login
      navigate('/login?redirect=/upgrade');
      return;
    }

    try {
      // Call backend to create Stripe checkout session
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/stripe/create-checkout-session/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        setError(data.error || 'Failed to create checkout session. Please try again.');
      }
    } catch (err) {
      console.error('Upgrade error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-20 px-6 bg-gradient-to-br from-primary to-slate-800 min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
            Upgrade to Pro
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Unlock unlimited change orders and take your contracting business to the next level.
          </p>
        </div>

        {/* Pricing Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden mb-8">
          <div className="bg-secondary p-12 text-center">
            <span className="inline-block bg-white text-secondary text-xs font-black px-4 py-2 rounded-full mb-4 tracking-widest uppercase">
              Pro Membership
            </span>
            <div className="text-white text-7xl font-black mb-2">$19</div>
            <div className="text-white/80 text-lg font-bold uppercase tracking-widest">
              Per Month
            </div>
          </div>

          <div className="p-12">
            <h3 className="text-2xl font-bold text-primary mb-8">
              Everything you need:
            </h3>

            <ul className="space-y-4 mb-12">
              <li className="flex items-center">
                <i className="fas fa-check-circle text-success mr-3 text-xl"></i>
                <span className="text-lg">Unlimited Change Orders</span>
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle text-success mr-3 text-xl"></i>
                <span className="text-lg">Unlimited Clients & Jobs</span>
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle text-success mr-3 text-xl"></i>
                <span className="text-lg">Digital Signature Capture</span>
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle text-success mr-3 text-xl"></i>
                <span className="text-lg">Professional PDF Generation</span>
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle text-success mr-3 text-xl"></i>
                <span className="text-lg">Email Delivery</span>
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle text-success mr-3 text-xl"></i>
                <span className="text-lg">Priority Support</span>
              </li>
              <li className="flex items-center">
                <i className="fas fa-check-circle text-success mr-3 text-xl"></i>
                <span className="text-lg">CSV Data Export</span>
              </li>
            </ul>

            {user?.is_premium ? (
              <div className="bg-success/10 border border-success text-success p-6 rounded-2xl text-center">
                <i className="fas fa-check-circle text-3xl mb-2"></i>
                <p className="font-bold text-lg">You're already a Pro member!</p>
                <button
                  onClick={() => navigate('/account')}
                  className="mt-4 text-primary hover:underline font-bold"
                >
                  Manage Subscription →
                </button>
              </div>
            ) : (
              <>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-xl mb-6 text-center">
                    {error}
                  </div>
                )}

                <button
                  onClick={handleUpgrade}
                  disabled={loading}
                  className="block w-full bg-secondary text-white py-5 rounded-2xl font-black text-xl text-center shadow-xl hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <i className="fas fa-circle-notch fa-spin mr-2"></i>
                      Loading...
                    </span>
                  ) : (
                    <>
                      <i className="fas fa-rocket mr-2"></i>
                      UPGRADE NOW
                    </>
                  )}
                </button>

                {!user && (
                  <p className="text-center text-sm text-slate-500 mt-4">
                    Don't have an account?{' '}
                    <button
                      onClick={() => navigate('/login')}
                      className="text-primary font-bold hover:underline"
                    >
                      Sign in
                    </button>{' '}
                    to continue
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 text-white">
          <h3 className="font-bold text-xl mb-4 text-center">Why Upgrade?</h3>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <i className="fas fa-infinity text-4xl text-secondary mb-3"></i>
              <p className="font-bold mb-1">Unlimited</p>
              <p className="text-sm text-white/80">No limits on orders or clients</p>
            </div>
            <div>
              <i className="fas fa-shield-check text-4xl text-secondary mb-3"></i>
              <p className="font-bold mb-1">Professional</p>
              <p className="text-sm text-white/80">Branded PDFs with signatures</p>
            </div>
            <div>
              <i className="fas fa-times-circle text-4xl text-secondary mb-3"></i>
              <p className="font-bold mb-1">Cancel Anytime</p>
              <p className="text-sm text-white/80">No contracts or commitments</p>
            </div>
          </div>
        </div>

        {/* Back link */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/')}
            className="text-white/60 hover:text-white transition text-sm"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </div>
  );
}

export default Upgrade;
